// targetProfitPresets — self-documenting presets for the per-game
// Target-Profit controls (P3, 2026-08-07).
//
// The admin GameConfigPanel used to render `target_profit_pct` as a bare
// 0–100 slider and `target_profit_enabled` as an unlabelled ON/OFF switch —
// leaving a non-expert owner guessing what "-10" or "40" actually does. These
// two builders turn those keys into ExplainedSetting preset controls (a row of
// plain-language chips + a Custom option + an always-visible consequence
// panel), matching the master casino-profit control and the cricket settings.
//
// Lives in shared-ui (NOT admin-frontend/src/components/settingPresets.ts)
// because the render site is ConfigField.tsx, a shared-ui component, and
// shared-ui must not import from admin-frontend.
//
// Money note: negative presets (a deliberate promotional house LOSS) are only
// offered when `allowNegative` is true. The engine is negative-capable + the
// value is triple-bounded to the -10% floor (UI custom min, server manifest
// min:-10, snapTargetPct read floor), so a fat-finger can never bleed past
// -10%. The Save path still gates a negative selection behind a confirm (see
// ConfigField.save()).

import type { ExplainedSettingProps, ExplainedPreset } from './ExplainedSetting';

// The Target-Profit engine treats target_profit_enabled truthily as
// true/'true'/1/'1' — mirror ConfigPanel.tsx's TP-7 banner logic exactly so
// the On/Off chip and the supersession banner never disagree.
export function coerceOn(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

// How the current target-profit value reads in the "Now:" badge.
function formatTargetPct(v: any): string {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return 'Break-even';
  return n < 0 ? `${n}% (house loss)` : `+${n}%`;
}

// The positive (house-earns) presets — always offered. Values bracket the
// live per-game settings (plinko 5, lucky7/rummy/xo 10, chess 20,
// carrom/ludo/snake_ladder/thimbles 15). Ordered low→high profit.
const POSITIVE_PRESETS: ExplainedPreset[] = [
  {
    label: 'Break-even', value: 0, tagline: '≈ 0%', tone: 'neutral',
    consequence:
      'This game aims to break even — players win about as much as they lose across the day. No profit, no loss; pure engagement.',
  },
  {
    label: 'Fair Play', value: 5, tagline: '≈ +5%', tone: 'safe',
    consequence:
      'Thin house edge (~5%). Players win plenty and the game feels fair, while you earn a small, steady profit.',
  },
  {
    label: 'Balanced', value: 15, tagline: '≈ +15%', tone: 'recommended',
    consequence:
      'Healthy profit (~15%) while players still win regularly across the 8 quarters. The sensible everyday setting.',
  },
  {
    label: 'Profit Focus', value: 25, tagline: '≈ +25%', tone: 'neutral',
    consequence:
      'Strong profit (~25%). Players win less often but still hit wins sometimes, so the game stays believable.',
  },
  {
    label: 'Max House', value: 40, tagline: '≈ +40%', tone: 'neutral',
    consequence:
      'Maximum profit (~40%). Players rarely win — this can slow play and word-of-mouth, so use it sparingly.',
  },
];

// The negative (house-spends) presets — a deliberate promotional loss so
// players net-win and spread the word. Only shown when allowNegative. Same
// values/tone as the master control's two loss presets.
const NEGATIVE_PRESETS: ExplainedPreset[] = [
  {
    label: "Players' Festival", value: -10, tagline: '≈ −10% (loss)', tone: 'risky',
    consequence:
      'Players WIN big — this game deliberately runs about a 10% loss. Pure goodwill / launch mode: the house is spending to pull players in. −10% is the deepest loss allowed.',
  },
  {
    label: 'Crowd-Pleaser', value: -5, tagline: '≈ −5% (loss)', tone: 'risky',
    consequence:
      'Players win noticeably more than they lose — this game runs about −5%. A gentle giveaway that keeps them coming back, at a small cost to you.',
  },
];

/**
 * ExplainedSetting props for a per-game `target_profit_pct` control.
 * @param allowNegative reveal the two promotional-loss presets + drop the
 *   custom minimum to -10. Keep false for a positive-only control.
 * @param game optional game id — appends a skill-game caveat to the deepest
 *   loss preset for chess/carrom/xo (their bots floor near break-even).
 */
export function targetProfitPctProps(
  value: any,
  onChange: (v: any) => void,
  opts?: { allowNegative?: boolean; game?: string },
): ExplainedSettingProps {
  const allowNegative = !!opts?.allowNegative;
  const skillGame = opts?.game === 'chess' || opts?.game === 'carrom' || opts?.game === 'xo';

  let negatives = NEGATIVE_PRESETS;
  if (allowNegative && skillGame) {
    // On skill games the bot edge floors near break-even, so a −10% target
    // under-delivers; be honest about it so the operator isn't surprised.
    negatives = NEGATIVE_PRESETS.map((p) =>
      p.value === -10
        ? { ...p, consequence: p.consequence + ' Note: on this skill game the bot can only be made so weak, so the real loss lands nearer break-even — the fleet average leans on Plinko / Lucky 7 / Thimbles.' }
        : p,
    );
  }

  const presets = allowNegative ? [...negatives, ...POSITIVE_PRESETS] : POSITIVE_PRESETS;

  return {
    title: 'Target profit for this game',
    help: 'How much this game aims to earn on average. The 8-quarter engine supplies the ups and downs, so players still win some quarters.',
    presets,
    value,
    onChange,
    formatValue: formatTargetPct,
    custom: {
      kind: 'number',
      min: allowNegative ? -10 : 0,
      max: 100,
      step: 5,
      unitSuffix: '%',
      placeholder: 'e.g. 15',
      describe: (v: any) => {
        const n = Number(v);
        if (!Number.isFinite(n) || n === 0) return 'This game aims to break even — no profit, no loss over the day.';
        if (n < 0) return `This game deliberately runs a promotional LOSS of about ${Math.abs(n)}% — players net-win. −10% is the deepest allowed; anything lower is floored to −10%.`;
        return `This game targets about a ${n}% profit on average across the 8 quarters — higher earns more but players win less often.`;
      },
    },
  };
}

/**
 * ExplainedSetting props for the per-game `target_profit_enabled` engine
 * switch — a two-chip On/Off control (boolean value). No custom option.
 */
export function targetProfitEnabledProps(
  value: any,
  onChange: (v: any) => void,
): ExplainedSettingProps {
  return {
    title: 'Auto-Profit engine',
    help: 'The master switch for this game’s automated Target-Profit control.',
    value: coerceOn(value),
    onChange,
    formatValue: (v: any) => (coerceOn(v) ? 'On' : 'Off'),
    presets: [
      {
        label: 'On — Auto-Profit', value: true, tagline: 'recommended', tone: 'recommended',
        consequence:
          'The Target-Profit engine runs this game: when a bot fills a seat, its play is steered each match to hit the % target above. This is the modern control and it replaces the older edge / bot-strength knobs further down.',
      },
      {
        label: 'Off — Manual', value: false, tagline: 'legacy', tone: 'neutral',
        consequence:
          'The engine is off; this game falls back to the legacy bot knobs (bot backfill, dice control, difficulty) below, and the % target above has no effect until this is On.',
      },
    ],
  };
}
